import React, { useEffect, useState } from 'react'

import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faBriefcase, faAddressCard } from '@fortawesome/free-solid-svg-icons';

import { RadioButton } from 'primereact/radiobutton';

// Services
import { getDropDownOptions } from '../../store/options/options.actions';

// Hooks
import InsertClientpartialData from '../../hooks/InsertClientpartialData';

// Components
import Button from '../../components/UI/Button/Button';
import Loader from "../../components/UI/Loader/Loader";

// Images
import icnAccountType from './Images/icn-account-type.png';

// Utilities
import { formatAccountProductOptions } from '../../utilities/helper-functions';

// Data
import { ACCOUNT_CATEGORY_ID, ACCOUNT_PRODUCT_NAMES } from '../../config/constants';

// Styles
import './Styles.scss';


export default function AccountType() {

  const { state } = useLocation();

  const {
    insertPartialDataIsLoading,
    insertPartialDataError,
    insertPartialData,
    handleInsertPartialData
  } = InsertClientpartialData();

  const [accountTypes, setAccountTypes] = useState([
    ACCOUNT_CATEGORY_ID.currentAccount,
    ACCOUNT_CATEGORY_ID.exteriorAccount
  ]);

  const [selectedProducts, setSelectedProducts] = useState([]);

  const [wantPointOfSale, setWantPointOfSale] = useState('no');

  const [accompaniment, setAccompaniment] = useState(null);

  const dropDownOptions = useSelector((state) => state.options.dropDownOptions);

  const consultClientPartialData = useSelector(
    (state) => state.forms.consultClientPartialData
  );

  const dispatch = useDispatch();

  const navigate = useNavigate();


  useEffect(() => {

    // dispatch(getDropDownOptions());

    const clientEmail = localStorage.getItem("clientEmail");

    if (clientEmail && state?.userPartialData?.checked_list) {

      dispatch(getDropDownOptions());

    } else {

      navigate(`/`);

    }

  }, [dispatch]);


  const handleAccountTypeSelection = (type) => {

    let accountValues = [...accountTypes];

    if (!accountValues?.includes(type)) {

      accountValues.push(type);

    } else {

      const index = accountValues?.indexOf(type);

      accountValues.splice(index, 1);

    }

    setAccountTypes(accountValues);

  };


  const isSameProduct = (a, b) => {

    return (
      a?.label === b?.label &&
      a?.categoryId === b?.categoryId
    );

  };


  const isCurrentLevelOne = (product) =>
    product?.label === ACCOUNT_PRODUCT_NAMES.currentLevelOneAccount;


  const hasCurrentLevelOneSelected = selectedProducts.some(
    item =>
      item.label === ACCOUNT_PRODUCT_NAMES.currentLevelOneAccount
  );


  const handleProductSelection = (product) => {

    const currentAccountProducts = formatAccountProductOptions(
      dropDownOptions?.data?.currentAccountProducts,
      ACCOUNT_CATEGORY_ID.currentAccount
    );

    const foreignProducts = formatAccountProductOptions(
      dropDownOptions?.data?.foreignCurrencyAccountProducts,
      ACCOUNT_CATEGORY_ID.exteriorAccount
    );


    const myCurrentAccountProduct = currentAccountProducts.find(
      item => item.label === ACCOUNT_PRODUCT_NAMES.myCurrentAccount
    );


    const foreignCurrentAccountProduct = foreignProducts.find(
      item => item.label === ACCOUNT_PRODUCT_NAMES.currentAccountMEDollars
    );


    const currentLevelOneAccountProduct = currentAccountProducts.find(
      item => item.label === ACCOUNT_PRODUCT_NAMES.currentLevelOneAccount
    );


    let selectedValues = [...selectedProducts];


    // Selecting Current Level One
    if (isCurrentLevelOne(product)) {
      const alreadySelected = selectedValues.some(item =>
        isSameProduct(item, product)
      );

      // Deselected -> return to defaults*
      if (alreadySelected) {
        const defaults = [];

        if (myCurrentAccountProduct) {
          defaults.push(myCurrentAccountProduct);
        }

        if (foreignCurrentAccountProduct) {
          defaults.push(foreignCurrentAccountProduct);
        }

        setSelectedProducts(defaults);
        return;
      }

      // Selected -> remove everything else*
      setSelectedProducts([currentLevelOneAccountProduct]);

      // Current Level One does not allow POS*
      setWantPointOfSale('no');
      setAccompaniment(null);

      return;
    }


    // If Current Level One is selected, block others
    if (hasCurrentLevelOneSelected) {
      return;
    }


    // Normal behavior
    const exists = selectedValues.some(item =>
      isSameProduct(item, product)
    );


    if (!exists) {

      selectedValues.push(product);

    } else {

      selectedValues = selectedValues.filter(
        item => !isSameProduct(item, product)
      );

    }


    setSelectedProducts(selectedValues);

  };


  // Insert My Current Account product if the user hasn't selected it
  // and selected a foreign currency product
  const handleInsertCurrentAccount = () => {

    handleInsertClientPartialData();

  };


  // Handle Insert Client Partial Data
  const handleInsertClientPartialData = () => {

    const data = {
      ...state?.userPartialData,
      products: selectedProducts,
      selected_product: true,
      wantPointOfSale: wantPointOfSale,
      accompaniment: wantPointOfSale === 'no' ? accompaniment : null
    };

    handleInsertPartialData(data);

  };


  useEffect(() => {
    if (insertPartialData) {
      navigate('/registro', {
        state: {
          userPartialData: {
            ...state?.userPartialData,
            products: selectedProducts,
            selected_product: true,
            wantPointOfSale,
            accompaniment: wantPointOfSale === 'no' ? accompaniment : null
          }
        }
      });
    }
  }, [insertPartialData, navigate, state, selectedProducts, wantPointOfSale, accompaniment]);


  // Set Current Level One as the default product
  useEffect(() => {

    if (!dropDownOptions?.data) return;

    if (selectedProducts.length > 0) return;


    const currentAccountProducts = formatAccountProductOptions(
      dropDownOptions?.data?.currentAccountProducts,
      ACCOUNT_CATEGORY_ID.currentAccount
    );


    const currentLevelOneAccountProduct = currentAccountProducts.find(
      item => item.label === ACCOUNT_PRODUCT_NAMES.currentLevelOneAccount
    );


    const defaults = [];

    if (currentLevelOneAccountProduct) {
      defaults.push(currentLevelOneAccountProduct);
    }


    setSelectedProducts(defaults);

  }, [dropDownOptions, selectedProducts.length]);


  const hasCurrentProducts =
    dropDownOptions?.data?.currentAccountProducts?.length > 0;

  const hasSavingProducts =
    dropDownOptions?.data?.savingAccountProducts?.length > 0;

  const hasForeignProducts =
    dropDownOptions?.data?.foreignCurrencyAccountProducts?.length > 0;


  const hasMyCurrentAccountSelected = selectedProducts?.some(
    item =>
      item.label === ACCOUNT_PRODUCT_NAMES.myCurrentAccount &&
      item.categoryId === ACCOUNT_CATEGORY_ID.currentAccount
  );


  const isCurrentAccountFlowValid =
    (hasCurrentLevelOneSelected || hasMyCurrentAccountSelected) &&
    (
      wantPointOfSale === 'si' ||
      (wantPointOfSale === 'no' && !!accompaniment)
    );

  return (

    <div className="page-wrapper">

      <div className="container">

        {(dropDownOptions?.isLoading || dropDownOptions?.error) &&

          <Loader
            message={'Cargando...'}
            error={dropDownOptions?.error}
            action={() => dispatch(getDropDownOptions())}
          />

        }


        {(!consultClientPartialData?.isLoading &&
          !dropDownOptions?.isLoading &&
          !dropDownOptions?.error) &&

          <div className='account-type'>

            <h1>
              Consulta aquí la información del tipo de cuenta que deseas abrir
            </h1>


            {insertPartialDataError &&
              <p className="form-error-message">
                {insertPartialDataError}
              </p>
            }


            <div className='account-types-list'>


              {/* Current Account */}

              <div className='account-types-entry-wrapper'>

                <button

                  disabled={!hasCurrentProducts}

                  onClick={() =>
                    handleAccountTypeSelection(
                      ACCOUNT_CATEGORY_ID.currentAccount
                    )
                  }

                  className={`account-types-entry 
                    ${accountTypes?.includes(
                    ACCOUNT_CATEGORY_ID.currentAccount
                  )
                      ? 'account-types-entry-active'
                      : ''
                    } 
                    ${!hasCurrentProducts
                      ? 'account-types-entry-disabled'
                      : ''
                    }`
                  }

                >

                  {accountTypes?.includes(
                    ACCOUNT_CATEGORY_ID.currentAccount
                  ) &&

                    <FontAwesomeIcon
                      className='account-types-entry-icon'
                      icon={faCircleCheck}
                    />

                  }


                  <div className='account-types-heading'>

                    <h2>Cuenta Corriente</h2>

                    <div>

                      <img
                        src={icnAccountType}
                        alt="Cuenta Corriente"
                      />

                    </div>

                  </div>


                  <p>
                    Es una cuenta de depósito no remunerada que se ajusta a tus
                    necesidades esenciales y mantiene tu dinero siempre a la vista.
                  </p>

                </button>


                {/* Product Subcategories */}

                {accountTypes?.includes(
                  ACCOUNT_CATEGORY_ID.currentAccount
                ) &&

                  <div className='account-types-subcategories'>

                    <ul>

                      {formatAccountProductOptions(
                        dropDownOptions?.data?.currentAccountProducts,
                        ACCOUNT_CATEGORY_ID.currentAccount
                      ).map((entry, i) =>

                        <div key={i}>

                          <ProductSubcateogryEntry

                            key={i}

                            entry={entry}

                            action={handleProductSelection}

                            isSelected={selectedProducts?.some(
                              item => isSameProduct(item, entry)
                            )}

                            disabled={
                              hasCurrentLevelOneSelected &&
                              entry.label !==
                              ACCOUNT_PRODUCT_NAMES.currentLevelOneAccount
                            }

                          />


                          {entry.label ===
                            ACCOUNT_PRODUCT_NAMES.myCurrentAccount &&

                            selectedProducts?.some(
                              item => isSameProduct(item, entry)
                            ) &&

                            <div className='input-container'>

                              <p>¿Quieres un Punto de Venta?</p>

                              <div className="radio-input-wrapper">


                                <div className='radio-input'>

                                  <RadioButton

                                    inputId="want-pos-si"

                                    name="want-pos"

                                    value="si"

                                    onChange={(e) =>
                                      setWantPointOfSale(e.value)
                                    }

                                    checked={
                                      wantPointOfSale === 'si'
                                    }

                                  />

                                  <label htmlFor="want-pos-si">
                                    Sí
                                  </label>

                                </div>


                                <div className='radio-input'>

                                  <RadioButton

                                    inputId="want-pos-no"

                                    name="want-pos"

                                    value="no"

                                    onChange={(e) =>
                                      setWantPointOfSale(e.value)
                                    }

                                    checked={
                                      wantPointOfSale === 'no'
                                    }

                                  />

                                  <label htmlFor="want-pos-no">
                                    No
                                  </label>

                                </div>


                              </div>

                            </div>

                          }

                        </div>

                      )}

                    </ul>

                  </div>

                }

              </div>


              {/* Saving Account */}

              <div className='account-types-entry-wrapper'>

                <button

                  disabled={!hasSavingProducts}

                  onClick={() =>
                    handleAccountTypeSelection(
                      ACCOUNT_CATEGORY_ID.savingAccount
                    )
                  }

                  className={`account-types-entry 
                    ${accountTypes?.includes(
                    ACCOUNT_CATEGORY_ID.savingAccount
                  )
                      ? 'account-types-entry-active'
                      : ''
                    } 
                    ${!hasSavingProducts
                      ? 'account-types-entry-disabled'
                      : ''
                    }`
                  }

                >

                  {accountTypes?.includes(
                    ACCOUNT_CATEGORY_ID.savingAccount
                  ) &&

                    <FontAwesomeIcon
                      className='account-types-entry-icon'
                      icon={faCircleCheck}
                    />

                  }


                  <div className='account-types-heading'>

                    <h2>Cuenta Ahorro</h2>

                    <div>

                      <img
                        src={icnAccountType}
                        alt="Cuenta Corriente"
                      />

                    </div>

                  </div>


                  <p>
                    Obtendrás intereses sobre saldo promedio mensual, sin tener
                    que utilizar una libreta para disponer del dinero de forma inmediata.
                  </p>

                </button>


                {/* Product Subcategories */}

                {accountTypes?.includes(
                  ACCOUNT_CATEGORY_ID.savingAccount
                ) &&

                  <div className='account-types-subcategories'>

                    <ul>

                      {formatAccountProductOptions(
                        dropDownOptions?.data?.savingAccountProducts,
                        ACCOUNT_CATEGORY_ID.savingAccount
                      ).map((entry, i) =>

                        <ProductSubcateogryEntry

                          key={i}

                          entry={entry}

                          action={handleProductSelection}

                          isSelected={selectedProducts?.some(
                            item => isSameProduct(item, entry)
                          )}

                          disabled={
                            hasCurrentLevelOneSelected &&
                            entry.label !==
                            ACCOUNT_PRODUCT_NAMES.currentLevelOneAccount
                          }

                        />

                      )}

                    </ul>

                  </div>

                }

              </div>


              {/* Foreign Currency Account */}

              <div className='account-types-entry-wrapper'>

                <button

                  disabled={!hasForeignProducts}

                  onClick={() =>
                    handleAccountTypeSelection(
                      ACCOUNT_CATEGORY_ID.exteriorAccount
                    )
                  }

                  className={`account-types-entry 
                    ${accountTypes?.includes(
                    ACCOUNT_CATEGORY_ID.exteriorAccount
                  )
                      ? 'account-types-entry-active'
                      : ''
                    } 
                    ${!hasForeignProducts
                      ? 'account-types-entry-disabled'
                      : ''
                    }`
                  }

                >

                  {accountTypes?.includes(
                    ACCOUNT_CATEGORY_ID.exteriorAccount
                  ) &&

                    <FontAwesomeIcon
                      className='account-types-entry-icon'
                      icon={faCircleCheck}
                    />

                  }


                  <div className='account-types-heading'>

                    <h2>Cuenta Moneda Extranjera</h2>

                    <div>

                      <img
                        src={icnAccountType}
                        alt="Cuenta Corriente"
                      />

                    </div>

                  </div>


                  <p>
                    Abre tu cuenta corriente no remunerada en Divisas, que te
                    garantiza el manejo de tus fondos en moneda extranjera.
                  </p>

                </button>


                {/* Product Subcategories */}

                {accountTypes?.includes(
                  ACCOUNT_CATEGORY_ID.exteriorAccount
                ) &&

                  <div className='account-types-subcategories'>

                    <ul>

                      {formatAccountProductOptions(
                        dropDownOptions?.data?.foreignCurrencyAccountProducts,
                        ACCOUNT_CATEGORY_ID.exteriorAccount
                      ).map((entry, i) =>

                        <ProductSubcateogryEntry

                          key={i}

                          entry={entry}

                          action={handleProductSelection}

                          isSelected={selectedProducts?.some(
                            item => isSameProduct(item, entry)
                          )}

                          disabled={
                            hasCurrentLevelOneSelected &&
                            entry.label !==
                            ACCOUNT_PRODUCT_NAMES.currentLevelOneAccount
                          }

                        />

                      )}

                    </ul>

                  </div>

                }

              </div>

            </div>


            {wantPointOfSale === 'no' &&

              <div className='current-account-questions'>

                <h2>
                  ¿Cómo prefieres que te acompañemos hoy?
                </h2>


                <div className="current-account-questions-entries">


                  <div
                    className={`current-account-questions-entry ${accompaniment === 'trámite particular'
                      ? 'current-account-questions-entry-active'
                      : ''
                      }`}

                    onClick={() =>
                      setAccompaniment('trámite particular')
                    }

                  >

                    <FontAwesomeIcon
                      className='current-account-question-icon'
                      icon={faAddressCard}
                    />


                    <div>

                      <p>
                        <strong>Trámite particular</strong>
                      </p>

                      <p>
                        No pertenezco a ninguna de las anteriores.
                      </p>

                    </div>


                    {accompaniment === 'trámite particular' &&

                      <FontAwesomeIcon
                        className='current-account-question-check'
                        icon={faCircleCheck}
                      />

                    }

                  </div>


                  <div
                    className={`current-account-questions-entry ${accompaniment === 'jornada'
                      ? 'current-account-questions-entry-active'
                      : ''
                      }`}

                    onClick={() =>
                      setAccompaniment('jornada')
                    }

                  >

                    <FontAwesomeIcon
                      className='current-account-question-icon'
                      icon={faBriefcase}
                    />


                    <div>

                      <p>
                        <strong>Estoy en una Jornada Laboral</strong>
                      </p>

                      <p>
                        Selecciona si estás en un operativo de tu empresa.
                      </p>

                    </div>


                    {accompaniment === 'jornada' &&

                      <FontAwesomeIcon
                        className='current-account-question-check'
                        icon={faCircleCheck}
                      />

                    }

                  </div>


                </div>

              </div>

            }


            {wantPointOfSale === 'si' && !hasCurrentLevelOneSelected &&

              <div className="info-card">

                <div className="info-icon">

                  <div className="icon-circle">

                    <span>i</span>

                  </div>

                </div>


                <div className="info-content">

                  <h3>
                    Para continuar con tu afiliación de Punto de Venta (POS),
                    solo necesitarás completar algunos recaudos adicionales al
                    momento de tu visita a la oficina: <strong>¡Buenas noticias!</strong>
                  </h3>


                  <p>
                    Los siguientes documentos ya los llevarás para formalizar
                    tu cuenta, por lo que no necesitas volver a presentarlos para el POS:
                  </p>


                  <ul>

                    <li>Doc de identidad</li>

                    <li>RIF vigente</li>

                    <li>Informe de atestiguamiento</li>

                  </ul>


                  <p>

                    <strong>Solo debes llevar adicionalmente:</strong>

                  </p>


                  <ul>

                    <li>
                      Declaración de ISLR de los últimos 3 años
                      (con soportes de pago o certificado electrónico)
                    </li>

                    <li>
                      Contrato de arrendamiento, documento de propiedad
                      u otro título sobre el local
                    </li>

                  </ul>

                </div>

              </div>

            }


            <Button

              type="submit"

              action={() => handleInsertCurrentAccount()}

              label={'Solicitar'}

              isLoading={insertPartialDataIsLoading}

              disabled={

                selectedProducts?.length === 0 ||

                insertPartialDataIsLoading ||

                !isCurrentAccountFlowValid

              }

            />

          </div>

        }

      </div>

    </div>

  )

}


const ProductSubcateogryEntry = ({
  entry,
  action,
  isSelected,
  disabled
}) => (

  <li>

    <button

      disabled={disabled}

      title={
        disabled
          ? 'Este producto es requerido para continuar'
          : ''
      }

      className={`${isSelected
        ? 'account-types-subcategory-active'
        : ''
        } ${disabled
          ? 'account-types-subcategory-disabled'
          : ''
        }`}

      onClick={() => {

        if (!disabled) {

          action(entry);

        }

      }}

    >

      <h3>{entry.label}</h3>

      <div>

        <img
          src={icnAccountType}
          alt={entry.label}
        />

      </div>

    </button>

  </li>

);
